// covered-by: frontends/carrie/src/utils/__tests__/askPunttMentionNotifications.test.ts
import {
  NotificationType,
  type Notification,
} from "@mg/schemas/src/commons/user";

export type AskPunttMentionTarget = {
  chatId: string;
  projectId?: string;
};

const PROJECT_CHAT_PATH =
  /^\/ask-puntt\/projects\/([^/?#]+)\/chats\/([^/?#]+)(?:[/?#]|$)/;
const STANDALONE_CHAT_PATH = /^\/ask-puntt\/([^/?#]+)(?:[/?#]|$)/;
// why: `/ask-puntt/projects` and `/ask-puntt/chats` are list routes, not chat
// ids; the standalone regex would otherwise read them as a chat called
// "projects" and hand the caller a dead link.
const NON_CHAT_SEGMENTS = new Set(["projects", "chats"]);

/** Chat (and project, for a project chat) that a mention CTA link points into. */
export function mentionNotificationTarget(
  ctaLink: string,
): AskPunttMentionTarget | null {
  let pathname: string;
  try {
    const base =
      typeof window === "undefined"
        ? "https://app.puntt.ai"
        : window.location.origin;
    pathname = new URL(ctaLink, base).pathname;
  } catch {
    return null;
  }
  const askPunttIndex = pathname.indexOf("/ask-puntt/");
  if (askPunttIndex < 0) return null;
  const askPunttPath = pathname.slice(askPunttIndex);

  const projectChat = askPunttPath.match(PROJECT_CHAT_PATH);
  if (projectChat?.[1] && projectChat[2]) {
    return { projectId: projectChat[1], chatId: projectChat[2] };
  }

  const standalone = askPunttPath.match(STANDALONE_CHAT_PATH);
  const chatId = standalone?.[1];
  if (!chatId || NON_CHAT_SEGMENTS.has(chatId)) return null;
  return { chatId };
}

export function isMentionNotification(notification: Notification): boolean {
  return notification.type === NotificationType.PROJECT_MENTION;
}

/**
 * Where a mention notification leads. christo stores the chat id on
 * `subReference` (new rows); older rows only carry it inside `ctaLink`, so the
 * link is parsed as the fallback and still supplies the project id.
 */
export function resolveMentionNotificationTarget(
  notification: Notification,
): AskPunttMentionTarget | null {
  const fromLink = notification.ctaLink
    ? mentionNotificationTarget(notification.ctaLink)
    : null;
  const subReference = notification.subReference?.trim();
  if (subReference) {
    return fromLink?.projectId
      ? { chatId: subReference, projectId: fromLink.projectId }
      : { chatId: subReference };
  }
  return fromLink;
}

// why "|": a delimiter-free join lets a longer value in one field plus a
// shorter one in the next produce the same string as the reverse split, which
// would silently swallow a distinct mention. Timestamps, ids and URLs never
// carry a literal pipe (a URL must percent-encode it), so it cannot be forged
// from inside a field.
const MENTION_KEY_SEPARATOR = "|";

/**
 * Stable identity for a mention row across profile polls.
 *
 * why no `message`: christo rewrites the copy on an *unread* mention row in
 * place once the agent answers (`rewriteMentionNotifications` sets
 * `notifications.$[nf].message` on the chat's unread PROJECT_MENTION rows), so
 * keying on it hands the same row a fresh identity on the very next poll and
 * the desktop toast fires a second time for one mention. Only the fields
 * christo never touches after the write identify the row.
 */
export function mentionNotificationKey(notification: Notification): string {
  return [
    String(notification.createdAt),
    String(notification.reference ?? ""),
    notification.subReference ?? "",
    notification.ctaLink ?? "",
  ].join(MENTION_KEY_SEPARATOR);
}
